import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '../services/api';
import { User, Team, UserRole, AuthResponse } from '../types';
import { prefetchEssentialData, refreshDataIfStale } from '../services/syncService';

interface AuthContextType {
  user: User | null;
  team: Team | null;
  modules: string[];
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  // Helper methods for checking permissions
  hasModule: (moduleId: string) => boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  isSuperAdmin: boolean;
  isTeamAdmin: boolean;
  isFieldAgent: boolean;
  // Refresh user data
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const setAuthData = useCallback((data: AuthResponse | null) => {
    if (data) {
      setUser(data.user);
      setTeam(data.team);
      setModules(data.modules);
      setToken(data.token);
      localStorage.setItem('token', data.token);
    } else {
      setUser(null);
      setTeam(null);
      setModules([]);
      setToken(null);
      localStorage.removeItem('token');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<AuthResponse>('/auth/me');
      setAuthData(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setAuthData(null);
    }
  }, [setAuthData]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          // Set token in state immediately to avoid flickers
          setToken(storedToken);
          // Fetch user details with team and modules
          const response = await api.get<AuthResponse>('/auth/me');
          setUser(response.data.user);
          setTeam(response.data.team);
          setModules(response.data.modules);
          // Update token if a new one was issued
          if (response.data.token !== storedToken) {
            localStorage.setItem('token', response.data.token);
            setToken(response.data.token);
          }
          
          // Refresh cached data if stale (runs in background)
          refreshDataIfStale().catch(err => 
            console.warn('Failed to refresh stale data:', err)
          );
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setTeam(null);
          setModules([]);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', { email, password });
      setAuthData(response.data);
      
      // Prefetch essential data for offline use (runs in background)
      prefetchEssentialData().catch(err => 
        console.warn('Failed to prefetch data:', err)
      );
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setAuthData(null);
    window.location.href = '/login';
  };

  // Helper: Check if team has a specific module enabled
  const hasModule = useCallback((moduleId: string): boolean => {
    // Super admins have implicit access to all modules
    if (user?.role === 'super_admin') return true;
    return modules.includes(moduleId);
  }, [user?.role, modules]);

  // Helper: Check if user has one of the specified roles
  const hasRole = useCallback((...roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  // Role convenience getters
  const isSuperAdmin = user?.role === 'super_admin';
  const isTeamAdmin = user?.role === 'team_admin' || user?.role === 'super_admin';
  const isFieldAgent = user?.role === 'field_agent';

  const value: AuthContextType = {
    user,
    team,
    modules,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasModule,
    hasRole,
    isSuperAdmin,
    isTeamAdmin,
    isFieldAgent,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

